import { Injectable, InjectionToken } from "@angular/core";
import { environment } from "@env/environment";
import { JwtHelperService } from "@auth0/angular-jwt";
import * as Raven from "raven-js";
// import * as LogRocket from 'logrocket';
import { I18nHttpClient } from "@core/services/i18n-http.service";
import { PlatformLocation } from "@angular/common";
import { TokenStorage } from "@core/services/token-storage.service";
import { catchError, filter, map, switchMap, tap } from "rxjs/operators";
import { Observable } from "rxjs/Observable";
import { HttpErrorResponse } from "@angular/common/http";
import { Store } from "@ngrx/store";
import * as fromRoot from "@core/store/reducers";
import { LogoutAction } from "@core/store/auth/auth.actions";

export const AUTHENTICATION_SERVICE = new InjectionToken(
  "AUTHENTICATION_SERVICE",
);

@Injectable()
export class AuthenticationService {
  private jwtHelper: JwtHelperService;
  private interruptedUrl: string;

  constructor(
    private http: I18nHttpClient,
    private platformLocation: PlatformLocation,
    private tokenStorage: TokenStorage,
    private store: Store<fromRoot.AppState>,
  ) {
    this.jwtHelper = new JwtHelperService();
  }

  /**
   * Check, if user already authenticated.
   * @description Should return Observable with true or false values
   * @returns {Observable<boolean>}
   * @memberOf AuthService
   */
  public isAuthenticated(): Observable<boolean> {
    return this.tokenStorage
      .getAccessToken()
      .pipe(map((token) => !!token && !this.jwtHelper.isTokenExpired(token)));
  }

  /**
   * Get access token
   * @description Should return access token in Observable from e.g.
   * localStorage
   * @returns {Observable<string>}
   */
  public getAccessToken(): Observable<string> {
    return this.tokenStorage.getAccessToken();
  }

  /**
   * Function, that should perform refresh token verifyTokenRequest
   * @description Should be successfully completed so interceptor
   * can execute pending requests or retry original one
   * @returns {Observable<any>}
   */
  public refreshToken(): Observable<any> {
    return this.tokenStorage.getRefreshToken().pipe(
      switchMap((refreshToken: string) => {
        if (refreshToken) {
          this.http.post(`${environment.API_ENDPOINT}/token/refresh`, {
            refresh_token: refreshToken,
          });
        } else {
          return Observable.of(null);
        }
      }),
      tap((response: any) => {
        if (response) {
          this.tokenStorage.setAccessToken(response.token);
          this.tokenStorage.setRefreshToken(response.refresh_token);
        }
      }),
      catchError((err) => {
        this.store.dispatch(new LogoutAction());

        return Observable.throw(err);
      }),
    );
  }

  /**
   * Function, checks response of failed request to determine,
   * whether token be refreshed or not.
   * @description Essentialy checks status
   * @param {Response} response
   * @returns {boolean}
   */
  public refreshShouldHappen(response: HttpErrorResponse): boolean {
    return response.status === 401;
  }

  public getInterruptedUrl(): string {
    return this.interruptedUrl;
  }

  public setInterruptedUrl(url: string): void {
    this.interruptedUrl = url;
  }

  /**
   * EXTRA AUTH METHODS
   */

  public login(username: string, password: string): Observable<any> {
    return this.http
      .post(`${environment.API_ENDPOINT}/login`, { username, password })
      .pipe(tap((response) => this.handleResponseData(response)));
  }

  public register(user): Observable<any> {
    return this.http.post(`${environment.API_ENDPOINT}/signup`, user);
  }

  public confirmUser(confirmationToken): Observable<any> {
    return this.http.post(`${environment.API_ENDPOINT}/signup/confirm`, {
      confirmationToken,
    });
  }

  public recoverPassword(email: string): Observable<any> {
    // add reset url to be included in email
    const data = {
      email,
      url:
        (this.platformLocation as any).location.origin +
        "/password-reset/recover/",
    };

    return this.http.post(
      `${environment.API_ENDPOINT}/password-reset/request`,
      data,
    );
  }

  public resetPassword(token: string, password: string): Observable<any> {
    return this.http.post(`${environment.API_ENDPOINT}/password-reset/reset`, {
      password,
      token,
    });
  }

  public logout(): void {
    this.tokenStorage.clear();
  }

  /***
   * Private Auxiliary methods
   */

  private handleResponseData(response) {
    this.tokenStorage.setAccessToken(response.token);
    this.tokenStorage.setRefreshToken(response.refresh_token);

    // save lang
    localStorage.setItem("lang", response.lang);

    // load user debug info
    const user = this.decodeToken(response.token);
    user.lang = response.lang;
    this.loadUserDebug(user);
  }

  private loadUserDebug(user) {
    Raven.setUserContext({
      email: user.username,
    });

    /*
        LogRocket.identify(user.username, {
            email: user.username,
        });
         */
  }

  private decodeToken(token: string) {
    return this.jwtHelper.decodeToken(token);
  }

  public loadFromStorage(): Observable<any> {
    // load jwt token from local storage
    return this.tokenStorage.getAccessToken().pipe(
      filter((token) => !!token && !this.jwtHelper.isTokenExpired(token)),
      map((token) => {
        const lang = localStorage.getItem("lang");

        // load user debug info
        const user = this.decodeToken(token);
        user.lang = lang;
        this.loadUserDebug(user);

        return user;
      }),
    );
  }
}



// WEBPACK FOOTER //
// /app/src/app/core/store/auth/authentication.service.ts